import { yupResolver } from "@hookform/resolvers/yup";
import React, { useState, createContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useMsg } from "../hooks/MsgProvider";
import { createTheme } from "@mui/material/styles";
import useSysParam from "hooks/UseSysParam";
import SignupPC from "view/pc/signup/Signup.pc";
import SignupMobile from "view/mobile/signup/Signup.mobile";
import RenderPcOrMobile from "./RenderPcOrMobile";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import { signup, getOrganizationListNotLogin, getByAgreementTypeNotLogin } from "api";
import { rsaOaepEncrypt } from "utils/encrypt";

export const SignupViewContext = createContext({});

const schema = yup.object().shape({
	email: yup.string().email("Please enter a valid email").required("Email is a required field"),
	lastName: yup
		.string()
		.required("Last Name is a required field")
		.transform((value) => value?.trim()),
	firstName: yup
		.string()
		.required("First Name is a required field")
		.transform((value) => value?.trim()),
	salutation: yup.string().required("Please select a salutation").typeError("Please select a salutation"),
	organizationId: yup.number().required().typeError("Please select an institution"),
	affiliationType: yup
		.string()
		.required("Affiliation Type is a required field")
		.typeError("Please select an affiliation type"),
	specify: yup
		.string()
		.when("organizationId", {
			is: (val) => [8, 9].includes(val),
			then: (schema) => schema.required("Specify is a required field"),
			otherwise: (schema) => schema.notRequired(),
		})
		.transform((value) => value?.trim()),
	mobileNumber: yup
		.string()
		.required("Must be a valid phone number (digits only)")
		.transform((value) => value?.trim())
		.test("is-positive-integer", "Must be a valid phone number (digits only)", (value) => {
			if (!value) return true;
			return /^\d+$/.test(value);
		}),
	countryCode: yup
		.string()
		.required("Country Code is a required field")
		.transform((value) => value?.trim())
		.test("is-positive-integer", "Must be a valid country code (digits only)", (value) => {
			if (!value) return true;
			return /^\d+$/.test(value);
		}),
	cardNumber: yup
		.string()
		.required("Student/Staff ID No. is a required field")
		.transform((value) => value?.trim()),
	jobTitle: yup
		.string()
		.required("Job Title is a required field")
		.transform((value) => value?.trim()),
	dept: yup
		.string()
		.required("Faculty/Dept is a required field")
		.transform((value) => value?.trim()),

	// cardExpiryDate: yup
	// 	.date()
	// 	.nullable()
	// 	.when("isPermanent", {
	// 		is: false,
	// 		then: yup
	// 			.date()
	// 			.typeError("Please enter or select a valid date")
	// 			.required("Role Validity is a required field"),
	// 	}),
	// isPermanent: yup.boolean(),
});

const theme = (themes) =>
	createTheme({
		...themes,
		components: {
			...themes.components,
			MuiInputBase: {
				styleOverrides: {
					input: {
						padding: 8,
					},
				},
			},
		},
	});

function Signup() {
	let history = useHistory();

	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const [isLoading, setIsLoading] = useState(false);

	const { setErrMsg } = useMsg();
	const [showLockTip, setShowLockTip] = useState(false);
	const [lockTipContent, setLockTipContent] = useState("");
	const { paramTextValue: logoLink } = useSysParam("sys_logo_hyperlink", true);
	const [loginRemainTime, setLoginRemainTime] = useState(0);
	const loginIntervalRef = React.useRef();
	const [agreePICS, setAgreePICS] = React.useState(false);
	const [specifyVisible, setSpecifyVisible] = useState(false); // display specify when "Others/Company" is selected for the organization item
	const [orgList, setOrgList] = useState([]); // organization list

	// Agreement
	const [openAgreement, setOpenAgreement] = useState(false);
	const [agreementTitle, setAgreementTitle] = useState("");
	const [agreementContent, setAgreementContent] = useState("");
	const [canAgree, setCanAgree] = useState(false);
	const [timeLeft, setTimeLeft] = useState(3);

	const fetchByAgreementType = async () => {
		try {
			const res = await getByAgreementTypeNotLogin({
				agreementType: "REGISTER",
			});
			if (res.data) {
				const { title, content } = res.data;
				setAgreementTitle(title);
				setAgreementContent(content);
			}
		} catch (error) {
			setErrMsg(error.message);
		}
	};

	const handleChangeAgreePICS = () => {
		if (!agreePICS) {
			setOpenAgreement(true);
		} else {
			setAgreePICS(!agreePICS);
		}
	};

	const handleAgree = (field, e) => {
		if (canAgree) {
			setAgreePICS(true);
			setOpenAgreement(false);
		}
	};

	useEffect(() => {
		if (openAgreement) {
			setCanAgree(false);
			const timer = setInterval(() => {
				setTimeLeft((prev) => {
					if (prev === 1) {
						clearInterval(timer);
						setCanAgree(true);
					}
					return prev - 1;
				});
			}, 1000);

			return () => clearInterval(timer);
		} else {
			setTimeLeft(3);
		}
	}, [openAgreement]);

	const onCloseAgreement = () => {
		setOpenAgreement(false);
	};

	const {
		register,
		handleSubmit,
		control,
		watch,
		formState: { errors },
		clearErrors,
		setValue,
	} = useForm({
		mode: "onChange",
		resolver: yupResolver(schema),
		defaultValues: {
			email: "",
			// plainPassword: "",
			// confirmPlainPassword: "",
			salutation: "",
			lastName: "",
			firstName: "",
			organizationId: "",
			specify: "",
			affiliationType: "",
			dept: "",
			countryCode: "",
			mobileNumber: "",
			jobTitle: "",
			cardNumber: "",
			// cardExpiryDate: null,
			// isPermanent: false,
		},
	});

	const organizationValue = watch("organizationId");
	// const permanentValue = watch("isPermanent");

	useEffect(() => {
		if (organizationValue == 9 || organizationValue == 8) {
			setSpecifyVisible(true);
		} else {
			setSpecifyVisible(false);
		}
	}, [organizationValue]);

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	// format data before request
	const formatData = async (data) => {
		const { mobileNumber, jobTitle, cardNumber, email } = data;
		const [encryptedMobileNumber, encryptedJobTitle, encryptedCardNumber, encryptedEmail] = await Promise.all([
			rsaOaepEncrypt(mobileNumber),
			rsaOaepEncrypt(jobTitle),
			rsaOaepEncrypt(cardNumber),
			rsaOaepEncrypt(email),
		]);
		const newData = {
			..._.cloneDeep(data),
			mobileNumber: encryptedMobileNumber,
			jobTitle: encryptedJobTitle,
			cardNumber: encryptedCardNumber,
			userEmail: encryptedEmail,
		};
		delete newData.email;
		return newData;
	};

	const onSubmit = async (data) => {
		const newData = await formatData(data);
		//console.log("onSubmit", newData);
		setIsLoading(true);
		setLoginRemainTime(5);
		try {
			const res = await signup(newData);
			//console.log(res);
			if (res.code === 200) {
				setLockTipContent("Your account has been created.");
				setShowLockTip(true);
			}
		} catch (error) {
			setErrMsg(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (loginRemainTime === 5) {
			clearInterval(loginIntervalRef.current);
			loginIntervalRef.current = setInterval(() => {
				setLoginRemainTime((prev) => prev - 1);
			}, 1000);
		}
		if (loginRemainTime === 0) {
			clearInterval(loginIntervalRef.current);
		}
	}, [loginRemainTime]);

	const getOrgList = async () => {
		try {
			const res = await getOrganizationListNotLogin();
			//console.log(res);
			if (res.data) {
				setOrgList(res.data);
			}
		} catch (error) {
			setErrMsg(error.message);
		}
	};

	useEffect(() => {
		getOrgList();
		fetchByAgreementType();
	}, []);

	const goBack = () => {
		history.goBack();
	};

	const handleSignupSuccess = () => {
		setShowLockTip(false);
		history.push("/");
	};

	return (
		<SignupViewContext.Provider
			value={{
				register,
				isLoading,
				theme,
				logoLink,
				handleSubmit,
				onSubmit,
				control,
				errors,
				handleClickShowPassword,
				handleClickShowConfirmPassword,
				handleMouseDownPassword,
				showPassword,
				showConfirmPassword,
				showLockTip,
				setShowLockTip,
				lockTipContent,
				loginRemainTime,
				agreePICS,
				handleChangeAgreePICS,
				specifyVisible,
				clearErrors,
				orgList,
				goBack,
				handleSignupSuccess,
				setValue,
				watch,
				openAgreement,
				onCloseAgreement,
				handleAgree,
				agreementTitle,
				agreementContent,
				canAgree,
				timeLeft,
			}}
		>
			<RenderPcOrMobile pc={<SignupPC />} mobile={<SignupMobile />} />
		</SignupViewContext.Provider>
	);
}
export default Signup;
